// TransferLocations.js
import React, { useState, useMemo, useContext, useEffect, useRef } from "react";
import api from "../api/index";
import { UserContext } from "../UserContext";
import "./styles/TransferLocations.css";
import { motion, useAnimation, useMotionValue } from "framer-motion";

const LOCATION_ID_KEYS = [
  "registration_id",
  "registrationId",
  "location_registration_id",
  "locationRegistrationId",
  "location_id",
  "locationId",
  "id",
];

const ACCEPTED_STATUS_KEYS = new Set([
  "accepted",
  "already_accepted",
  "complete",
  "completed",
  "approved",
  "finalized",
]);

const DENIED_STATUS_KEYS = new Set([
  "denied",
  "declined",
  "rejected",
  "cancelled",
  "canceled",
]);

const TRUTHY_ACCEPT_VALUES = new Set(["true", "1", "yes", "y"]);

const normalizeLocationId = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "object") {
    for (const key of LOCATION_ID_KEYS) {
      if (value[key] !== null && value[key] !== undefined) {
        return String(value[key]).trim();
      }
    }

    if (value.location) {
      return normalizeLocationId(value.location);
    }

    return null;
  }

  return String(value).trim();
};

const getLocationPayloadId = (loc) => {
  for (const key of LOCATION_ID_KEYS) {
    if (loc && loc[key] !== null && loc[key] !== undefined) {
      return loc[key];
    }
  }
  return normalizeLocationId(loc);
};

const isTruthyFlag = (value, truthyKeySet = new Set()) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    const underscored = normalized.replace(/\s+/g, "_");

    if (
      TRUTHY_ACCEPT_VALUES.has(normalized) ||
      TRUTHY_ACCEPT_VALUES.has(underscored) ||
      truthyKeySet.has(normalized) ||
      truthyKeySet.has(underscored)
    ) {
      return true;
    }
  }

  return false;
};

const isTruthyAcceptFlag = (value) => isTruthyFlag(value, ACCEPTED_STATUS_KEYS);
const isTruthyDeniedFlag = (value) => isTruthyFlag(value, DENIED_STATUS_KEYS);

const normalizeLocationList = (list) =>
  Array.isArray(list) ? list.filter(Boolean) : [];

const buildLocationMap = (transfer) => {
  const map = new Map();
  const addLocations = (locations) => {
    normalizeLocationList(locations).forEach((loc) => {
      const id = normalizeLocationId(loc);
      if (id && !map.has(id)) {
        map.set(id, loc);
      }
    });
  };

  addLocations(transfer.pending_locations);
  addLocations(transfer.accepted_locations);
  addLocations(transfer.denied_locations);
  addLocations(transfer.locations);

  return map;
};

function TransferCardShell({
  transferId,
  className,
  cardInactive,
  isActive,
  activeRect,
  onActivate,
  onDragStateChange,
  dragConstraints,
  children,
}) {
  const controls = useAnimation();
  const cardRef = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const [isOffset, setIsOffset] = useState(false);
  const shouldFloat = Boolean(isActive && activeRect);

  useEffect(() => {
    if (!isActive) {
      x.set(0);
      y.set(0);
      setIsOffset(false);
      controls.start({ scale: 1 });
      return;
    }

    controls.start({ scale: isOffset ? 1.3 : 1 });
  }, [controls, isActive, isOffset, x, y]);

  const updateOffsetState = () => {
    const nextOffset = Math.abs(x.get()) > 1 || Math.abs(y.get()) > 1;
    setIsOffset(nextOffset);
  };

  return (
    <motion.div
      key={transferId}
      ref={cardRef}
      className={className}
      style={{
        cursor: cardInactive ? "not-allowed" : "grab",
        zIndex: isActive ? 10 : 1,
        position: shouldFloat ? "fixed" : "relative",
        top: shouldFloat ? activeRect?.top : undefined,
        left: shouldFloat ? activeRect?.left : undefined,
        width: shouldFloat ? activeRect?.width : undefined,
        x,
        y,
      }}
      drag={!cardInactive}
      dragElastic={0.65}
      dragMomentum={false}
      dragConstraints={dragConstraints}
      animate={controls}
      initial={false}
      onDragStart={() => onDragStateChange(true)}
      onDrag={updateOffsetState}
      onDragEnd={() => {
        updateOffsetState();
        onDragStateChange(false);
      }}
      onPointerDown={() => {
        if (!cardInactive && !isActive) {
          const rect = cardRef.current?.getBoundingClientRect() || null;
          onActivate(transferId, rect);
        }
      }}
    >
      {children}
    </motion.div>
  );
}

function TransferLocations({
  transfers = [],
  onTransfersUpdated, // optional callback to refetch or update parent state
}) {
  const [selectedByTransfer, setSelectedByTransfer] = useState({});
  const [loadingTransferId, setLoadingTransferId] = useState(null);

  // toast instead of modal
  const [toast, setToast] = useState({
    visible: false,
    type: "success", // "success" | "error"
    message: "",
  });

  const [activeTab, setActiveTab] = useState("incoming"); // "incoming" | "outgoing"
  const { user } = useContext(UserContext);
  const [activeCardId, setActiveCardId] = useState(null);
  const [activeCardRect, setActiveCardRect] = useState(null);
  const [isDraggingCard, setIsDraggingCard] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!activeCardId) return;
      const handlePointerDown = (event) => {
        const cardEl = event.target.closest(".transfer-card");
        if (!cardEl) {
          setActiveCardId(null);
          setActiveCardRect(null);
        }
      };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [activeCardId]);

  // ---------- toast helpers ----------

  const showToast = (type, message) => {
    setToast({ visible: true, type, message });
    setTimeout(() => {
      setToast((prev) => ({ ...prev, visible: false }));
    }, 3000);
  };

  const hideToast = () => {
    setToast((prev) => ({ ...prev, visible: false }));
  };

  // ---------- helpers ----------

  const normalizeStatus = (status) =>
    (status ?? "").toString().trim().toLowerCase();

  const toStatusKey = (status) =>
    normalizeStatus(status).replace(/\s+/g, "_"); // "Partially accepted" -> "partially_accepted"

  // Nicely format status text like "partially_accepted" -> "Partially accepted"
  const formatStatusText = (status) => {
    const norm = normalizeStatus(status).replace(/_/g, " ");
    if (!norm) return "";
    return norm.charAt(0).toUpperCase() + norm.slice(1);
  };

  const getDisplayStatusKey = (transfer) =>
    toStatusKey(transfer.display_status || transfer.status);

  // Is an individual location already accepted in this transfer?
  // (Covers several common backend conventions so you don't have to rename fields.)
  const matchesTransferIdLists = (lists, id) => {
    for (const list of lists) {
      if (!Array.isArray(list)) continue;
      const normalizedList = list
        .map((entry) => normalizeLocationId(entry))
        .filter(Boolean);
      if (normalizedList.includes(id)) {
        return true;
      }
    }
    return false;
  };

  const isLocationAlreadyAccepted = (transfer, loc) => {
    const id = normalizeLocationId(loc);
    if (!id) {
      return false;
    }

    // 1) Transfer-level arrays of accepted IDs (common patterns)
    const transferAcceptedLists = [
      transfer.accepted_location_ids,
      transfer.accepted_locations,
      transfer.already_accepted_location_ids,
      transfer.already_accepted_locations,
      transfer.accepted_ids,
    ];

    if (matchesTransferIdLists(transferAcceptedLists, id)) {
      return true;
    }

    // 2) Boolean flags on the location
    const acceptFlagKeys = ["is_accepted", "accepted", "already_accepted"];
    if (acceptFlagKeys.some((key) => isTruthyAcceptFlag(loc?.[key]))) {
      return true;
    }

    // 3) Status-like fields on the location
    const locFlags = [
      loc.transfer_status,
      loc.status,
      loc.location_status,
      loc.transferStatus,
      loc.locationStatus,
      loc.transfer_state,
      loc.state,
    ]
      .filter(Boolean)
      .map((s) => toStatusKey(s));

    if (locFlags.some((key) => ACCEPTED_STATUS_KEYS.has(key))) {
      return true;
    }

    return false;
  };

  const isLocationDenied = (transfer, loc) => {
    const id = normalizeLocationId(loc);
    if (!id) {
      return false;
    }

    const transferDeniedLists = [
      transfer.denied_location_ids,
      transfer.denied_locations,
      transfer.rejected_location_ids,
      transfer.rejected_locations,
      transfer.declined_location_ids,
      transfer.declined_locations,
      transfer.denied_ids,
    ];

    if (matchesTransferIdLists(transferDeniedLists, id)) {
      return true;
    }

    const deniedFlagKeys = [
      "is_denied",
      "denied",
      "already_denied",
      "is_rejected",
      "rejected",
      "already_rejected",
      "declined",
    ];

    if (deniedFlagKeys.some((key) => isTruthyDeniedFlag(loc?.[key]))) {
      return true;
    }

    const locFlags = [
      loc.transfer_status,
      loc.status,
      loc.location_status,
      loc.transferStatus,
      loc.locationStatus,
      loc.transfer_state,
      loc.state,
    ]
      .filter(Boolean)
      .map((s) => toStatusKey(s));

    if (locFlags.some((key) => DENIED_STATUS_KEYS.has(key))) {
      return true;
    }

    return false;
  };

  const isLocationFinalized = (transfer, loc) =>
    isLocationAlreadyAccepted(transfer, loc) || isLocationDenied(transfer, loc);

  // ---------- selection helpers ----------

  const getSelectableLocationIds = (transfer) => {
    if (
      Array.isArray(transfer.pending_location_ids) &&
      transfer.pending_location_ids.length > 0
    ) {
      return Array.from(
        new Set(
          transfer.pending_location_ids
            .map((value) => normalizeLocationId(value))
            .filter(Boolean)
        )
      );
    }

    const sourceLocations =
      transfer.pending_locations || transfer.locations || [];

    return sourceLocations
      .filter((loc) => !isLocationFinalized(transfer, loc))
      .map((loc) => normalizeLocationId(loc))
      .filter(Boolean);
  };

  const handleToggleLocation = (transferId, location) => {
    const locationId = normalizeLocationId(location);
    if (!locationId) return;

    setSelectedByTransfer((prev) => {
      const existing = (prev[transferId] || []).map((id) => String(id));
      const isSelected = existing.includes(locationId);
      const updated = isSelected
        ? existing.filter((id) => id !== locationId)
        : [...existing, locationId];

      return {
        ...prev,
        [transferId]: updated,
      };
    });
  };

  const handleToggleAllLocations = (transfer) => {
    // Only consider locations that are NOT already processed
    const allSelectableIds = getSelectableLocationIds(transfer);

    setSelectedByTransfer((prev) => {
      const existing = (prev[transfer.id] || []).map((id) => String(id));
      const allSelected =
        allSelectableIds.length > 0 &&
        allSelectableIds.every((id) => existing.includes(id));

      return {
        ...prev,
        [transfer.id]: allSelected ? [] : allSelectableIds,
      };
    });
  };

  const getSelectedLocationIds = (transfer) => {
    const raw = selectedByTransfer[transfer.id] || [];
    return Array.from(
      new Set(
        raw
          .filter((id) => id !== null && id !== undefined && id !== "")
          .map((id) => String(id))
      )
    );
  };

  // ---------- API actions ----------

  const handleRespond = async (transfer, action) => {
    const selectedLocationIds = getSelectedLocationIds(transfer);
    const pendingIdsSet = new Set(getSelectableLocationIds(transfer));
    const locationMap = buildLocationMap(transfer);

    const validLocationEntries = selectedLocationIds
      .map((id) => {
        if (!pendingIdsSet.has(id)) {
          return null;
        }
        const matchingLoc = locationMap.get(id);
        if (!matchingLoc) {
          return null;
        }

        return {
          normalizedId: id,
          payloadId: getLocationPayloadId(matchingLoc),
        };
      })
      .filter(Boolean);

    const validLocationIds = validLocationEntries.map(
      (entry) => entry.payloadId
    );
    const normalizedValidIds = validLocationEntries.map(
      (entry) => entry.normalizedId
    );

    if (normalizedValidIds.length !== selectedLocationIds.length) {
      setSelectedByTransfer((prev) => ({
        ...prev,
        [transfer.id]: normalizedValidIds,
      }));
    }

    if (!validLocationIds.length) {
      showToast(
        "error",
        "Please select at least one location before responding."
      );
      return;
    }

    setLoadingTransferId(transfer.id);

    try {
      const response = await fetch(`${api.main}api/agent/respond_transfer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_token: user.access_token,
          sagent_id: encodeURIComponent(user.id),
          transfer_id: transfer.id,
          action, // "accept" or "deny"
          selected_location_ids: validLocationIds,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        showToast("error", data.error || "Unable to respond to transfer.");
      } else {
        showToast(
          "success",
          data.message || `Transfer ${action}ed successfully.`
        );
        onTransfersUpdated && onTransfersUpdated();
      }
    } catch (err) {
      showToast("error", "Network error while responding to transfer.");
    } finally {
      setLoadingTransferId(null);
    }
  };

  const handleCancel = async (transfer) => {
    setLoadingTransferId(transfer.id);

    try {
      const response = await fetch(`${api.main}api/agent/cancel_transfer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_token: user.access_token,
          sagent_id: encodeURIComponent(user.id),
          transfer_id: transfer.id,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        showToast("error", data.error || "Unable to cancel transfer.");
      } else {
        showToast("success", data.message || "Transfer canceled successfully.");
        onTransfersUpdated && onTransfersUpdated();
      }
    } catch (err) {
      showToast("error", "Network error while canceling transfer.");
    } finally {
      setLoadingTransferId(null);
    }
  };

  // ---------- filtering into sections ----------

  const incomingTransfers = useMemo(
    () => transfers.filter((t) => t.direction === "incoming"),
    [transfers]
  );

  const outgoingTransfers = useMemo(
    () => transfers.filter((t) => t.direction === "outgoing"),
    [transfers]
  );

  // Incoming buckets
  const incomingActive = useMemo(
    () =>
      incomingTransfers.filter(
        (t) => getDisplayStatusKey(t) === "active"
      ),
    [incomingTransfers]
  );

  const incomingAccepted = useMemo(
    () =>
      incomingTransfers.filter(
        (t) => getDisplayStatusKey(t) === "accepted"
      ),
    [incomingTransfers]
  );

  const incomingDeniedOrCanceled = useMemo(
    () =>
      incomingTransfers.filter((t) => {
        const key = getDisplayStatusKey(t);
        return key === "denied" || key === "canceled";
      }),
    [incomingTransfers]
  );

  const expiredIncoming = useMemo(
    () =>
      incomingTransfers.filter(
        (t) => getDisplayStatusKey(t) === "expired"
      ),
    [incomingTransfers]
  );

  // Outgoing buckets
  const activeOutgoing = useMemo(
    () =>
      outgoingTransfers.filter(
        (t) => getDisplayStatusKey(t) === "active"
      ),
    [outgoingTransfers]
  );

  const outgoingAccepted = useMemo(
    () =>
      outgoingTransfers.filter(
        (t) => getDisplayStatusKey(t) === "accepted"
      ),
    [outgoingTransfers]
  );

  const outgoingDeniedOrCanceled = useMemo(
    () =>
      outgoingTransfers.filter((t) => {
        const key = getDisplayStatusKey(t);
        return key === "denied" || key === "canceled";
      }),
    [outgoingTransfers]
  );

  const expiredOutgoing = useMemo(
    () =>
      outgoingTransfers.filter(
        (t) => getDisplayStatusKey(t) === "expired"
      ),
    [outgoingTransfers]
  );

  const hasAnyIncoming =
    incomingTransfers && incomingTransfers.length > 0;
  const hasAnyOutgoing =
    outgoingTransfers && outgoingTransfers.length > 0;

  // ---------- card renderer ----------

  const renderTransferCard = (transfer) => {
    const isIncoming = transfer.direction === "incoming";
    const selectedIds = getSelectedLocationIds(transfer);
    const isLoading = loadingTransferId === transfer.id;

    const displayStatusRaw = transfer.display_status || transfer.status || "";
    const statusKey = toStatusKey(displayStatusRaw);
    const statusClass = `transfer-status-pill transfer-status-pill--${
      statusKey || "default"
    }`;

    const canRespond = Boolean(transfer.can_respond);
    const canCancel = Boolean(transfer.can_cancel);
    const cardInactive =
      (!canRespond && !canCancel) || statusKey === "expired";

    const selectableIds = getSelectableLocationIds(transfer);
    const pendingIdsSet = new Set(selectableIds);
    const allSelected =
      canRespond &&
      selectableIds.length > 0 &&
      selectableIds.every((id) => selectedIds.includes(id));

    const locationMap = buildLocationMap(transfer);
    const fallbackLocations = Array.from(locationMap.values());

    const getLocationsWithFallback = (preferred, fallbackFn) => {
      const normalized = normalizeLocationList(preferred);
      return normalized.length > 0 ? normalized : fallbackFn();
    };

    const pendingLocations = getLocationsWithFallback(
      transfer.pending_locations,
      () => {
        if (pendingIdsSet.size) {
          return Array.from(pendingIdsSet)
            .map((id) => locationMap.get(id))
            .filter(Boolean);
        }
        return fallbackLocations.filter(
          (loc) => !isLocationFinalized(transfer, loc)
        );
      }
    );

    const acceptedLocations = getLocationsWithFallback(
      transfer.accepted_locations,
      () =>
        fallbackLocations.filter((loc) =>
          isLocationAlreadyAccepted(transfer, loc)
        )
    );

    const deniedLocations = getLocationsWithFallback(
      transfer.denied_locations,
      () => fallbackLocations.filter((loc) => isLocationDenied(transfer, loc))
    );

    const renderLocationRow = (loc, variant) => {
      const locationId = normalizeLocationId(loc);
      if (!locationId) return null;

      const line1 = loc.address || "";
      const line2Parts = [
        loc.city,
        loc.state && loc.zipcode
          ? `${loc.state} ${loc.zipcode}`
          : loc.state || "",
      ].filter(Boolean);
      const line2 = line2Parts.join(", ");
      const fullAddress = [line1, line2].filter(Boolean).join(" ");

      if (variant === "pending") {
        if (!canRespond) {
          return (
            <div key={locationId} className="transfer-location-item">
              <div className="transfer-location-name">
                {loc.business_name}
              </div>
              <div className="transfer-location-address">{fullAddress}</div>
            </div>
          );
        }

        const checked =
          canRespond && locationId && selectedIds.includes(locationId);
        return (
          <div key={locationId} className="transfer-location-item">
            <label>
              <input
                type="checkbox"
                checked={checked}
                onChange={() => handleToggleLocation(transfer.id, loc)}
              />{" "}
              <span className="transfer-location-name">
                {loc.business_name}
              </span>
              <br />
              <span className="transfer-location-address">{fullAddress}</span>
            </label>
          </div>
        );
      }

      const classNames = ["transfer-location-item"];
      if (variant === "accepted") {
        classNames.push("transfer-location-item--accepted");
      } else if (variant === "denied") {
        classNames.push("transfer-location-item--denied");
      }

      return (
        <div key={locationId} className={classNames.join(" ")}>
          <div className="transfer-location-name">{loc.business_name}</div>
          <div className="transfer-location-address">{fullAddress}</div>
        </div>
      );
    };

    const renderLocationGroup = (
      title,
      locations,
      variant,
      emptyMessage
    ) => (
      <div className="transfer-location-group">
        <div className="transfer-location-subtitle">
          {title} ({locations.length})
        </div>
        {locations.length === 0 ? (
          <div className="transfer-locations-empty">{emptyMessage}</div>
        ) : (
          locations.map((loc) => renderLocationRow(loc, variant))
        )}
      </div>
    );

    const cardClassList = ["transfer-card"];
    if (cardInactive) {
      cardClassList.push("transfer-card--inactive");
    }
    const cardClassName = cardClassList.join(" ");
    const isActive = activeCardId === transfer.id;

    return (
      <TransferCardShell
        key={transfer.id}
        transferId={transfer.id}
        className={cardClassName}
        cardInactive={cardInactive}
        isActive={isActive}
        activeRect={isActive ? activeCardRect : null}
        onActivate={(id, rect) => {
          setActiveCardId(id);
          setActiveCardRect(rect);
        }}
        onDragStateChange={setIsDraggingCard}
        dragConstraints={panelRef}
      >
        {/* header row */}
        <div className="transfer-card-header">
          <div className="transfer-card-header-block">
            <div className="transfer-label">Transfer ID</div>
            <div className="transfer-value transfer-id">{transfer.id}</div>
          </div>

          <div className="transfer-card-header-block">
            <div className="transfer-label">Status</div>
            <div className="transfer-status-wrapper">
              <span className={statusClass}>
                {formatStatusText(displayStatusRaw)}
              </span>
            </div>
          </div>
          {!cardInactive && (
            <span className="transfer-card-drag-icon" aria-hidden="true">
              {!isActive && "⠿"}
            </span>
          )}
        </div>

        {/* agents row – conditional */}
        <div className="transfer-card-agents">
          {transfer.direction === "incoming" && (
            <div className="transfer-agent-block">
              <div className="transfer-label">From Agent</div>
              <div className="transfer-value">
                {transfer.from_sagent_name}
              </div>
              <div className="transfer-subvalue">
                {transfer.from_sagent_email}
              </div>
            </div>
          )}

          {transfer.direction === "outgoing" && (
            <div className="transfer-agent-block">
              <div className="transfer-label">To Agent</div>
              <div className="transfer-value">{transfer.to_sagent_name}</div>
              <div className="transfer-subvalue">
                {transfer.to_sagent_email}
              </div>
            </div>
          )}
        </div>

        {/* locations */}
        <div className="transfer-card-body">
          <div className="transfer-locations-block">
            <div className="transfer-label">
              Locations ({transfer.location_count})
            </div>

            {canRespond && (
              <div className="transfer-select-all">
                <label>
                  <input
                    type="checkbox"
                    disabled={selectableIds.length === 0}
                    checked={allSelected}
                    onChange={() => handleToggleAllLocations(transfer)}
                  />{" "}
                  Select all pending
                </label>
              </div>
            )}

            <div className="transfer-locations-list show-scrollbar">
              {renderLocationGroup(
                "Pending Locations",
                pendingLocations,
                "pending",
                "No pending locations."
              )}
              {renderLocationGroup(
                "Accepted Locations",
                acceptedLocations,
                "accepted",
                "No accepted locations."
              )}
              {renderLocationGroup(
                "Denied Locations",
                deniedLocations,
                "denied",
                "No denied locations."
              )}
            </div>
          </div>
        </div>

        {/* actions */}
        <div className="transfer-actions-block">
          <div className="transfer-label">Actions</div>

          <div className="transfer-actions-buttons">
            {canRespond && (
              <>
                <button
                  disabled={isLoading || !canRespond}
                  onClick={() => handleRespond(transfer, "accept")}
                  className="btn btn-primary"
                >
                  {isLoading ? "Processing..." : "Accept"}
                </button>
                <button
                  disabled={isLoading || !canRespond}
                  onClick={() => handleRespond(transfer, "deny")}
                  className="btn btn-delete"
                >
                  {isLoading ? "Processing..." : "Deny"}
                </button>
              </>
            )}

            {canCancel && (
              <button
                disabled={isLoading || !canCancel}
                onClick={() => handleCancel(transfer)}
                className="btn btn-delete"
              >
                {isLoading ? "Processing..." : "Cancel Transfer"}
              </button>
            )}

            {!canRespond && !canCancel && (
              <div className="transfer-no-actions">No actions available.</div>
            )}
          </div>
        </div>
      </TransferCardShell>
    );
  };

  const incomingSections = [
    {
      key: "incoming-active",
      title: "Active",
      items: incomingActive,
      empty: "No active incoming transfers.",
    },
    {
      key: "incoming-accepted",
      title: "Accepted",
      items: incomingAccepted,
      empty: "No accepted incoming transfers.",
    },
    {
      key: "incoming-denied",
      title: "Denied / Canceled",
      items: incomingDeniedOrCanceled,
      empty: "No denied or canceled incoming transfers.",
    },
    {
      key: "incoming-expired",
      title: "Expired",
      items: expiredIncoming,
      empty: "No expired incoming transfers.",
    },
  ];

  const outgoingSections = [
    {
      key: "outgoing-active",
      title: "Active",
      items: activeOutgoing,
      empty: "No active outgoing transfers.",
    },
    {
      key: "outgoing-accepted",
      title: "Accepted",
      items: outgoingAccepted,
      empty: "No accepted outgoing transfers.",
    },
    {
      key: "outgoing-denied",
      title: "Denied / Canceled",
      items: outgoingDeniedOrCanceled,
      empty: "No denied or canceled outgoing transfers.",
    },
    {
      key: "outgoing-expired",
      title: "Expired",
      items: expiredOutgoing,
      empty: "No expired outgoing transfers.",
    },
  ];

  const tabLabel = activeTab === "incoming" ? "Incoming" : "Outgoing";
  const hasAny = activeTab === "incoming" ? hasAnyIncoming : hasAnyOutgoing;
  const emptyCopy =
    activeTab === "incoming"
      ? "No incoming transfers found."
      : "No outgoing transfers found.";

  const activeSections = activeTab === "incoming" ? incomingSections : outgoingSections;

  const tabStats =
    activeTab === "incoming"
      ? {
          Active: incomingActive.length,
          Accepted: incomingAccepted.length,
          "Denied / Canceled": incomingDeniedOrCanceled.length,
          Expired: expiredIncoming.length,
        }
      : {
          Active: activeOutgoing.length,
          Accepted: outgoingAccepted.length,
          "Denied / Canceled": outgoingDeniedOrCanceled.length,
          Expired: expiredOutgoing.length,
        };

  const renderSections = (sections) =>
    sections.map(({ key, title, items, empty }) => (
      <div key={key} className="transfer-section-block">
        <div className="section-heading">
          <div>
            <p className="section-eyebrow">Status</p>
            <h3>{title}</h3>
          </div>
          <span className="section-subtitle">
            {items.length} {items.length === 1 ? "transfer" : "transfers"}
          </span>
        </div>
        {items.length === 0 ? (
          <p className="transfer-empty-submessage">{empty}</p>
        ) : (
          <div className="transfer-section-scroll show-scrollbar">
            <div className="transfer-grid">{items.map(renderTransferCard)}</div>
          </div>
        )}
      </div>
    ));

  // ---------- main render ----------

  return (
    <div className="transfer-page transfer-locations">
      {isDraggingCard && <div className="transfer-drag-overlay" />}
      {/* Toast */}
      {toast.visible && (
        <div
          className={`transfer-toast transfer-toast--${toast.type}`}
          onClick={hideToast}
        >
          {toast.message}
        </div>
      )}

      <div className="page-header">
        <div>
          <p className="page-eyebrow">Transfers</p>
          <h2>Transfer Requests</h2>
          <p className="section-subtitle">
            Review requests between ISO partners and respond faster with clear status
            buckets.
          </p>
        </div>
        <div className="page-pill">{tabLabel} queue</div>
      </div>

      <section ref={panelRef} className="page-section transfer-panel">
        <div className="transfer-tabs">
          <button
            type="button"
            onClick={() => setActiveTab("incoming")}
            className={`transfer-tab ${
              activeTab === "incoming" ? "transfer-tab--active" : ""
            }`}
          >
            Incoming
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("outgoing")}
            className={`transfer-tab ${
              activeTab === "outgoing" ? "transfer-tab--active" : ""
            }`}
          >
            Outgoing
          </button>
        </div>

        <div className="transfer-summary-grid">
          {Object.entries(tabStats).map(([label, value]) => (
            <div key={label} className="transfer-summary-card">
              <span className="transfer-summary-label">{label}</span>
              <span className="transfer-summary-value">{value}</span>
            </div>
          ))}
        </div>

        {!hasAny && (
          <p className="transfer-empty-message">{emptyCopy}</p>
        )}

        {hasAny && <div className="transfer-section-collection">{renderSections(activeSections)}</div>}
      </section>
    </div>
  );
}

export default TransferLocations;
